import { useTranslation, setAppLanguage } from '@/i18n/useTranslation';
import type { Language } from '@/i18n/translations';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '@/store/AppContext';
import { getSsoProfileUrl } from '@/config/hosts';
import { switchLocaleInPath } from '@/i18n/localeRouting';
import { useAppNavigate } from '@/hooks/useAppNavigate';

export default function SettingsPage() {
  const { t, language } = useTranslation();
  const { isAuthenticated } = useApp();
  const location = useLocation();
  const navigate = useAppNavigate();
  const administrationSettingsUrl = useMemo(
    () => getSsoProfileUrl(window.location.href, ''),
    []
  );

  const setLanguage = (next: Language) => {
    if (next !== language) {
      setAppLanguage(next);
      navigate(switchLocaleInPath(location.pathname, location.search, location.hash, next), { replace: true });
    }
  };

  return (
    <div className="h-full w-full bg-bg-main overflow-y-auto">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-text-primary mb-1">{t('settings.title')}</h1>
          <p className="text-sm text-text-secondary">{t('settings.subtitle')}</p>
        </div>

        <div className="rounded-xl border border-border bg-bg-panel px-4 py-4">
          <div className="text-sm font-medium text-text-primary mb-3">{t('language.label')}</div>
          <div className="flex gap-2">
            {(['en', 'pl'] as Language[]).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setLanguage(lang)}
                className={`h-9 px-4 rounded-full text-sm font-medium transition-all border ${
                  language === lang
                    ? 'bg-accent text-bg-main border-accent'
                    : 'bg-bg-control text-text-secondary border-border hover:text-text-primary'
                }`}
              >
                {t(lang === 'en' ? 'language.en' : 'language.pl')}
              </button>
            ))}
          </div>
        </div>

        {isAuthenticated && (
          <div className="rounded-xl border border-accent/30 bg-accent/10 px-4 py-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-text-primary">{t('settings.ssoNotice')}</p>
              <a
                href={administrationSettingsUrl}
                className="inline-flex h-9 items-center justify-center rounded-full border border-accent/40 bg-bg-main px-4 text-sm font-semibold text-text-primary hover:bg-bg-control transition-colors"
              >
                {t('settings.openSso')}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
